import { useState, useEffect, useCallback, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "../../../AuthContext";
import { FACTORY_SYMBOL_CODES } from "utils/symbols";
import { COLORS } from "theme/tokens";

// ─── 常數 ─────────────────────────────────────────────────────
const RISK_LABELS = { LOW: "低風險 守護者", MID: "中風險 領航者", HIGH: "高風險 獵鷹" };
const RISK_COLORS = { LOW: "#2196f3", MID: COLORS.warning, HIGH: COLORS.error };
const FREQ_LABELS = {
  D1_FREQ:   "日線 D1",
  HIGH_FREQ: "高頻 M15",
  LOW_FREQ:  "低頻 H1",
  M5_FREQ:   "測試 M5",
  M1_FREQ:   "測試 M1",
};
// ✔ P2 優化：移除硬編碼的 RISK_KPI，從後端動態載入
// SYMBOLS 改 import 共用常數 utils/symbols（2026-05-19 商品池擴張 4→8）
const SYMBOLS = FACTORY_SYMBOL_CODES;   // 後台工廠用完整商品池（含指數/能源/加密）
const RISKS = ["LOW", "MID", "HIGH"];
const FREQS = ["D1_FREQ", "HIGH_FREQ", "LOW_FREQ", "M5_FREQ", "M1_FREQ"];
// 各商品可用頻率白名單：**由後端 /config 供給**（見 symbolFreqs state）。
//   ⚠️ 2026-08-25：這裡原本是手抄的第二份，停在 D1 上線前 —— XAUUSD 被標成不支援 D1
//      （實際支援）、OILCash/US100Cash/BTCUSD 等 D1 商品整個不在表裡，導致勾選頻率時
//      顯示的「⚠️ 某某商品會被跳過」提示整組是錯的。不要再在前端硬寫一份。

// ─── 工具函式 ──────────────────────────────────────────────────
const statusChip = (status) => {
  const map = {
    QUEUED: { label: "排隊中", color: COLORS.grayDark },
    RUNNING: { label: "生產中", color: "#2196f3" },
    DONE: { label: "完成", color: COLORS.safe },
    FAILED: { label: "失敗", color: COLORS.error },
    CANCELLED: { label: "已取消", color: COLORS.warning }, // ✔ P3 新增
  };
  const c = map[status] || { label: status, color: COLORS.grayDark };
  return <Chip label={c.label} size="small" sx={{ bgcolor: c.color, color: COLORS.white, fontWeight: 700 }} />;
};

const fmtDate = (d) => d ? new Date(d).toLocaleString("zh-TW", { hour12: false }) : "—";
const fmtKpi = (kpi, key) => kpi?.[key] != null ? Number(kpi[key]).toFixed(2) : "—";
export const kpiPass = (kpi, risk, freq, config, symbol) => {
  if (!kpi || !config) return false;
  const symbolConfig = config[symbol] || config["DEFAULT"];
  const ruleSet = symbolConfig[risk]?.[freq] || config["DEFAULT"]?.[risk]?.[freq];

  if (!ruleSet) return false;

  const metrics = ["returnRatePct", "maxDrawdownPct", "profitFactor", "calmarRatio"];
  for (const key of metrics) {
    const rule = ruleSet[key];
    if (!rule) continue;
    const val = kpi[key];
    if (val == null) continue; // calmarRatio 可能舊資料沒有，跳過而非失敗
    if (rule.type === "gt" && val < rule.value) return false;
    if (rule.type === "lt" && val > rule.value) return false;
  }
  return true;
};

// ─── 主頁面 ────────────────────────────────────────────────────
function BankFactory() {
  const { token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [burnHours, setBurnHours] = useState(0);   // 🔥 連續燒時數（0=只跑一輪；>0 一輪跑完自動接下一輪直到時數到）
  const [cancelling, setCancelling] = useState(false); // ✔ P3 新增
  const [reclassifying, setReclassifying] = useState(false);
  const [reclassifyStatus, setReclassifyStatus] = useState(null); // { status, result, error }
  const reclassifyRetryRef = useRef(0); // 輪詢計數器，防止永久輪詢
  const [symbolsKpiConfig, setSymbolsKpiConfig] = useState(null); // ✔ 終極版：全幣別三維配置
  const [codeDialog, setCodeDialog] = useState({ open: false, data: null, fetching: false });
  // 🚀 LIVE 轉換對話框
  const [liveDialog, setLiveDialog] = useState({
    open: false, converting: false, saving: false,
    source: null,
    form: { volume: 0.1, capital: 10000, filename: "" },
    result: null,
    saveMsg: null
  });
  // 🚀 LIVE 庫總覽 dialog
  // 🚀 LIVE 模擬倉狀態 + 📦 EXE 打包都已移到 Beast 菁英策略庫頁面（bank-elite）
  // 📦 EXE 打包功能已移到 Beast 菁英策略庫頁面（/admin/bank-elite）
  const [audience, setAudience] = useState("EXTERNAL"); // INTERNAL | EXTERNAL
  const [kpiConfigs, setKpiConfigs] = useState({ INTERNAL: null, EXTERNAL: null, RPO_RECENT: null });
  const [symbolFreqs, setSymbolFreqs] = useState({});   // 各商品可用頻率，由 /config 供給
  // initial state 從 SYMBOLS 動態 derive — 8 個商品全部預設 true，避免日後新增商品要兩處改
  const [customSym, setCustomSym] = useState(
    SYMBOLS.reduce((acc, s) => { acc[s] = true; return acc; }, {})
  );
  const [customRisk, setCustomRisk] = useState({ LOW: true, MID: true, HIGH: true });
  // EXTERNAL 預設只跑 HIGH_FREQ + LOW_FREQ，INTERNAL 全部
  // M5_FREQ / M1_FREQ 預設關閉（測試用途，需手動開啟）
  const [customFreq, setCustomFreq] = useState({ HIGH_FREQ: true, LOW_FREQ: true, M5_FREQ: false, M1_FREQ: false });

  const headers = useCallback(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  // ── 取得 Job 列表 ──
  const fetchJobs = useCallback(async () => {
    try {
      const r = await fetch("/api/admin/bank-factory/jobs", { headers: headers() });
      const d = await r.json();
      setJobs(Array.isArray(d) ? d : []);
    } catch { }
  }, [headers]);

  // 從後端載入兩套 KPI 門檻（INTERNAL + EXTERNAL）
  const fetchConfig = useCallback(async () => {
    try {
      const r = await fetch("/api/admin/bank-factory/config", { headers: headers() });
      const d = await r.json();
      const configs = {
        INTERNAL: d.INTERNAL_SYMBOLS_KPI_CONFIG || null,
        EXTERNAL: d.EXTERNAL_SYMBOLS_KPI_CONFIG || d.SYMBOLS_KPI_CONFIG || null,
        RPO_RECENT: d.RPO_RECENT_KPI_CONFIG || null,
      };
      setKpiConfigs(configs);
      setSymbolFreqs(d.SYMBOL_FREQUENCIES || {});
      // 向後兼容：同時更新舊的 state
      setSymbolsKpiConfig(configs.EXTERNAL);
    } catch { }
  }, [headers]);

  // ── 取得 Job 詳情（輪詢用）──
  const fetchJobDetail = useCallback(async (jobId) => {
    try {
      const r = await fetch(`/api/admin/bank-factory/jobs/${jobId}`, { headers: headers() });
      const d = await r.json();
      setJobDetail(d);
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...d } : j));
      // ✔ P3 優化： CANCELLED 也要停止輪詢
      if (d.status === "DONE" || d.status === "FAILED" || d.status === "CANCELLED") {
        setActiveJob(null);
        fetchJobs(); // 任務結束時刷新列表
      }
    } catch { }
  }, [headers]);

  // ── 初始載入 ──
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchJobs(), fetchConfig()]).finally(() => setLoading(false));
  }, [fetchJobs, fetchConfig]); // ✔ P2：同時載入 KPI 配置

  // ── 輪詢中的 Job ──
  useEffect(() => {
    if (!activeJob) return;
    fetchJobDetail(activeJob);
    const timer = setInterval(() => fetchJobDetail(activeJob), 3000);
    return () => clearInterval(timer);
  }, [activeJob, fetchJobDetail]);

  // ── 啟動工廠 ──
  const handleLaunch = async () => {
    const symbols = SYMBOLS.filter(s => customSym[s]);
    const riskLevels = RISKS.filter(r => customRisk[r]);
    const frequencies = FREQS.filter(f => customFreq[f]);
    // 兩種模式都自動分類風險，不需要手動選擇
    if (!symbols.length || !frequencies.length) {
      alert("請至少選擇一個貨幣對和頻率");
      return;
    }
    setLaunching(true);
    try {
      const r = await fetch("/api/admin/bank-factory/jobs", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ audience, symbols, riskLevels, frequencies, triggeredBy: "admin", burnHours }),
      });
      const d = await r.json();
      if (d.jobId) {
        await fetchJobs();
        setActiveJob(d.jobId);
        alert(
          burnHours > 0
            ? `🔥 連續燒已啟動！JobID: ${d.jobId}\n總格子數：${d.totalCells}\n\n將連續跑 ${burnHours} 小時：一輪跑完自動接下一輪，時間到自動停。\n要提早結束，按當前任務的「取消」即可。`
            : `🚀 工廠已啟動！JobID: ${d.jobId}\n總格子數：${d.totalCells}`
        );
      }
    } catch (e) {
      alert("啟動失敗：" + e.message);
    } finally {
      setLaunching(false);
    }
  };

  // ── 查看 Job 詳情 ──
  const handleViewJob = (jobId) => {
    setActiveJob(jobId);
    fetchJobDetail(jobId);
  };

  // ── 查看策略代碼 ──
  const handleViewCode = async (candidateId) => {
    setCodeDialog({ open: true, data: null, fetching: true });
    try {
      const r = await fetch(`/api/admin/bank-factory/candidates/${candidateId}/code`, { headers: headers() });
      const d = await r.json();
      setCodeDialog({ open: true, data: d, fetching: false });
    } catch {
      setCodeDialog({ open: true, data: null, fetching: false });
    }
  };

  // ── 🚀 開啟 LIVE 轉換對話框 ──
  const handleOpenLiveConvert = () => {
    if (!codeDialog.data?.code) return;
    const d = codeDialog.data;
    const sid = (d.id || "").slice(0, 8);
    const defaultName = `${d.symbol}_${d.riskLevel || "AUTO"}_${sid}_LIVE.py`;
    // 從 header 推 volume，若策略代碼有 "Lot size: X" 就抓
    const lotMatch = d.code.match(/Lot size[:：]\s*([\d.]+)/);
    const defaultVolume = lotMatch ? Number(lotMatch[1]) : 0.1;
    setLiveDialog({
      open: true, converting: false, saving: false,
      source: { code: d.code, symbol: d.symbol, frequency: d.frequency, strategyId: d.id },
      form: { volume: defaultVolume, capital: 10000, filename: defaultName },
      result: null,
      saveMsg: null
    });
  };

  // ── 執行轉換 ──
  const handleRunConvert = async () => {
    if (!liveDialog.source) return;
    setLiveDialog(s => ({ ...s, converting: true, saveMsg: null }));
    try {
      // 從 frequency 推 timeframe
      const tfMap = { D1_FREQ: "D1", HIGH_FREQ: "M15", LOW_FREQ: "H1", M5_FREQ: "M5", M1_FREQ: "M1" };
      const timeframe = tfMap[liveDialog.source.frequency] || "H1";
      const r = await fetch("/api/admin/bank-factory/convert-to-live", {
        method: "POST",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({
          code: liveDialog.source.code,
          symbol: liveDialog.source.symbol,
          timeframe,
          volume: Number(liveDialog.form.volume),
          capital: Number(liveDialog.form.capital),
          strategyId: liveDialog.source.strategyId
        })
      });
      const d = await r.json();
      setLiveDialog(s => ({ ...s, converting: false, result: d }));
    } catch (e) {
      setLiveDialog(s => ({ ...s, converting: false, saveMsg: "轉換失敗：" + e.message }));
    }
  };

  // ── 儲存到 paper_trade_live/ ──
  const handleSaveLive = async () => {
    if (!liveDialog.result?.liveCode) return;
    setLiveDialog(s => ({ ...s, saving: true, saveMsg: null }));
    try {
      const r = await fetch("/api/admin/bank-factory/save-to-live", {
        method: "POST",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({
          code: liveDialog.result.liveCode,
          filename: liveDialog.form.filename
        })
      });
      const d = await r.json();
      if (d.ok) {
        const valOk = d.validation?.ok;
        setLiveDialog(s => ({
          ...s, saving: false,
          saveMsg: valOk
            ? `✅ 已存到 ${d.path}（py_compile OK）`
            : `⚠️ 已存到 ${d.path}，但語法驗證失敗：${d.validation?.error || ""}`
        }));
      } else {
        setLiveDialog(s => ({ ...s, saving: false, saveMsg: "❌ 儲存失敗：" + (d.error || "") }));
      }
    } catch (e) {
      setLiveDialog(s => ({ ...s, saving: false, saveMsg: "❌ 儲存失敗：" + e.message }));
    }
  };

  // ── 🚀 開啟 LIVE 庫總覽 ──
  // 📦 EXE 打包邏輯已移到 Beast 菁英策略庫頁面（bank-elite/index.js）
  //   架構：工廠 = 生產線（出策略），菁英庫 = 倉儲管理（含 EXE 打包、活躍狀態）

  // LIVE 模擬倉狀態 / 批次補齊 handlers 已搬到 bank-elite/index.js

  // ── 取消任務 ──
  const handleCancel = async (jobId) => {
    if (!window.confirm("確定要取消此工廠任務？Worker 將在當前格子完成後停止，已产生的候選策略會保留。")) return;
    setCancelling(true);
    try {
      const r = await fetch(`/api/admin/bank-factory/jobs/${jobId}/cancel`, {
        method: "PATCH",
        headers: headers(),
      });
      const d = await r.json();
      if (d.ok) {
        await fetchJobs();
        fetchJobDetail(jobId);
      } else {
        alert("取消失敗：" + (d.error || "未知錢誤誤"));
      }
    } catch (e) {
      alert("取消失敗：" + e.message);
    } finally {
      setCancelling(false);
    }
  };

  // ── 重新分類候選策略庫 ──
  const handleReclassify = async () => {
    if (!window.confirm("確定要重新分類所有候選策略庫？系統將對符合新 KPI 標準的候選者執行 OOS 考試並晉升到精英庫。此操作可能需要數分鐘。")) return;
    setReclassifying(true);
    setReclassifyStatus({ status: "QUEUED" });
    reclassifyRetryRef.current = 0; // 重置計數器
    try {
      const r = await fetch("/api/admin/bank-factory/reclassify-candidates", {
        method: "POST",
        headers: headers(),
      });
      const d = await r.json();
      if (!d.ok) {
        setReclassifyStatus({ status: "FAILED", error: d.error || "觸發失敗" });
        setReclassifying(false);
      }
      // 開始輪詢進度
    } catch (e) {
      setReclassifyStatus({ status: "FAILED", error: e.message });
      setReclassifying(false);
    }
  };

  // ── 輪詢 reclassify 進度（含 5 分鐘超時保護）──
  const RECLASSIFY_MAX_RETRY = 75; // 4s × 75 = 300s = 5 分鐘
  const pollReclassifyStatus = useCallback(async () => {
    reclassifyRetryRef.current += 1;
    if (reclassifyRetryRef.current > RECLASSIFY_MAX_RETRY) {
      setReclassifyStatus({ status: "TIMEOUT", error: "超過 5 分鐘未完成，請重整頁面確認狀態" });
      setReclassifying(false);
      return;
    }
    try {
      const r = await fetch("/api/admin/bank-factory/reclassify-candidates/status", { headers: headers() });
      const d = await r.json();
      setReclassifyStatus(d);
      if (d.status === "DONE" || d.status === "FAILED" || d.status === "IDLE") {
        setReclassifying(false);
      }
    } catch { }
  }, [headers]);

  useEffect(() => {
    if (!reclassifying) return;
    const timer = setInterval(pollReclassifyStatus, 4000);
    return () => clearInterval(timer);
  }, [reclassifying, pollReclassifyStatus]);

  // ── 刪除 Job ──
  const handleDelete = async (jobId) => {
    if (!window.confirm("確定要刪除此工廠任務及所有候選策略嗎？")) return;
    await fetch(`/api/admin/bank-factory/jobs/${jobId}`, { method: "DELETE", headers: headers() });
    setJobDetail(null);
    setActiveJob(null);
    fetchJobs();
  };

  // ── 9 宮格矩陣顯示（針對選中的 Job）──
  const Matrix9 = ({ candidates = [], jobSymbols = [], jobAudience = "EXTERNAL" }) => {
    const activeConfig = kpiConfigs[jobAudience] || symbolsKpiConfig;
    const cellMap = {};
    candidates.forEach(c => {
      const key = `${c.symbol}_${c.riskLevel}_${c.frequency}`;
      if (!cellMap[key]) cellMap[key] = [];
      cellMap[key].push(c);
    });

    // 優先使用任務設定的商品，若無則從候選人中提取，最後才用預設
    const displaySymbols = jobSymbols.length > 0 ? jobSymbols : (candidates.length > 0 ? [...new Set(candidates.map(c => c.symbol))] : SYMBOLS);

    // 只顯示 H1 + D1，H4 不再生成
    const matrixFreqs = FREQS.filter(f => f !== "MID_FREQ");
    return (
      <MDBox sx={{ overflowX: "auto", mt: 2 }}>
        {displaySymbols.map(sym => (
          <MDBox key={sym} mb={2}>
            <MDTypography variant="subtitle2" fontWeight="bold" mb={1} sx={{ color: COLORS.grayDark }}>
              {sym}
            </MDTypography>
            <MDBox display="grid" sx={{ gridTemplateColumns: `120px repeat(${matrixFreqs.length}, 1fr)`, gap: "6px" }}>
              <MDBox />
              {matrixFreqs.map(f => (
                <MDBox key={f} sx={{ textAlign: "center", p: 0.5, bgcolor: COLORS.grayLightest, borderRadius: 1 }}>
                  <MDTypography variant="caption" fontWeight="bold">{FREQ_LABELS[f]}</MDTypography>
                </MDBox>
              ))}
              {RISKS.map(risk => (
                <>
                  <MDBox key={risk} sx={{ display: "flex", alignItems: "center", p: 0.5 }}>
                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: RISK_COLORS[risk] }}>
                      {RISK_LABELS[risk]}
                    </MDTypography>
                  </MDBox>
                  {matrixFreqs.map(freq => {
                    const allInCell = cellMap[`${sym}_${risk}_${freq}`] || [];

                    // 🚨 前端主動選 Top 2：優先級 Qualified > Potential > Score
                    let cells = [...allInCell]
                      .sort((a, b) => {
                        if (a.isQualified !== b.isQualified) return a.isQualified ? -1 : 1;
                        if (a.isPotential !== b.isPotential) return a.isPotential ? -1 : 1;
                        return (a.score || 999999) - (b.score || 999999);
                      })
                      .slice(0, 2);

                    // 如果還是沒有 (代表還在跑)，就顯示最新的一個
                    if (cells.length === 0 && allInCell.length > 0) {
                      const latest = [...allInCell].sort((a, b) => b.iterNo - a.iterNo)[0];
                      cells = [latest];
                    }

                    const hasCells = cells.length > 0;

                    return (
                      <MDBox
                        key={freq}
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          bgcolor: !hasCells ? COLORS.grayLightest : COLORS.white,
                          border: `1px solid ${!hasCells ? "#ddd" : COLORS.grayLighter}`,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1
                        }}
                      >
                        {/* 狀態標示 */}
                        {(() => {
                          if (!hasCells) return <MDTypography variant="caption" sx={{ color: COLORS.black, textAlign: "center", fontWeight: "bold" }}>⏳ 等待中</MDTypography>;

                          // 如果 Job 已經結束，則全部顯示已結束
                          const isJobDone = jobDetail?.status === "DONE" || jobDetail?.status === "FAILED" || jobDetail?.status === "CANCELLED";
                          const hasQualified = allInCell.some(c => c.isQualified);
                          const maxIter = Math.max(...allInCell.map(c => c.iterNo || 0));

                          if (isJobDone || hasQualified || maxIter >= 15) {
                            return <MDTypography variant="caption" sx={{ color: COLORS.safe, textAlign: "center", fontWeight: "bold" }}>🏁 已結束</MDTypography>;
                          }
                          return <MDTypography variant="caption" sx={{ color: "#1976d2", textAlign: "center", fontWeight: "bold" }}>🔄 迭代中 (第{maxIter}代)</MDTypography>;
                        })()}

                        {!hasCells ? (
                          <MDBox sx={{ py: 1, textAlign: "center" }}>
                            <MDTypography variant="caption" sx={{ color: "#bbb" }}>—</MDTypography>
                          </MDBox>
                        ) : (
                          cells.map((cell, idx) => {
                            const pass = kpiPass(cell.kpi, risk, freq, activeConfig, cell.symbol);
                            return (
                              <Tooltip
                                key={cell.id}
                                title={
                                  <div>
                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>策略 #{idx + 1}</div>
                                    <div>回報: {(() => {
                                      const trades = cell.kpi?.trades || cell.kpi?.number_of_trades || 0;
                                      if (cell.kpi?.returnRatePct === 0 && trades === 0) return "🚫 無交易";
                                      return `${fmtKpi(cell.kpi, "returnRatePct")}%`;
                                    })()}</div>
                                    <div>MDD: {fmtKpi(cell.kpi, "maxDrawdownPct")}%</div>
                                    {cell.metadata?.stability_score !== undefined && (
                                      <div style={{ color: cell.metadata.stability_score >= 80 ? COLORS.warning : COLORS.safe, fontWeight: "bold" }}>
                                        穩定度: {cell.metadata.stability_score}%
                                      </div>
                                    )}
                                    <div>迭代: {cell.iterNo} 次</div>
                                    {cell.isPotential && (
                                      <div style={{ color: "#2196f3", fontWeight: "bold", marginTop: "4px" }}>
                                        🌱 潛力苗子 (可供孵化)
                                      </div>
                                    )}
                                    {cell.isDuplicate && (
                                      <div style={{ color: "#ff5252", fontWeight: "bold", marginTop: "4px" }}>
                                        ⚠️ 相關性過高 (拒絕晉升)
                                      </div>
                                    )}
                                    {cell.criticInsight && (
                                      <div style={{ fontSize: "0.75rem", fontStyle: "italic", marginTop: "4px", borderTop: "1px solid #555", pt: 1 }}>
                                        💡 {cell.criticInsight.substring(0, 150)}...
                                      </div>
                                    )}
                                  </div>
                                }
                                arrow
                              >
                                <MDBox
                                  onClick={() => handleViewCode(cell.id)}
                                  sx={{
                                    p: 0.8,
                                    borderRadius: 1,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    bgcolor: pass ? "#e8f5e9" : COLORS.lighter,
                                    border: `1px solid ${pass ? COLORS.safe : COLORS.warning}`,
                                    transition: "all 0.2s",
                                    "&:hover": { transform: "scale(1.02)", boxShadow: 2, zIndex: 1 },
                                  }}
                                >
                                  <MDTypography variant="caption" display="block" fontWeight="bold"
                                    sx={{ color: pass ? COLORS.safe : (cell.isPotential ? "#1565c0" : "#e65100"), fontSize: "0.85rem" }}>
                                    #{idx + 1} {pass ? "✅" : (cell.isPotential ? "🌱" : "⚠️")} {(() => {
                                      const trades = cell.kpi?.trades || cell.kpi?.number_of_trades || 0;
                                      if (cell.kpi?.returnRatePct === 0 && trades === 0) return "🚫 無交易";
                                      return `${fmtKpi(cell.kpi, "returnRatePct")}%`;
                                    })()}
                                  </MDTypography>
                                </MDBox>
                              </Tooltip>
                            );
                          })
                        )}
                      </MDBox>
                    );
                  })}
                </>
              ))}
            </MDBox>
          </MDBox>
        ))}
      </MDBox>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={4}>
        <Grid container spacing={3}>

          {/* ── 頂部：專家戰略建議 ── */}
          <Grid item xs={12}>
            <MDBox
              sx={{
                background: "linear-gradient(135deg, rgba(45, 211, 155, 0.1) 0%, rgba(33, 150, 243, 0.1) 100%)",
                borderRadius: "16px",
                p: 3,
                mb: 1,
                border: "1px solid rgba(45, 211, 155, 0.3)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
              }}
            >
              <MDTypography variant="h6" color="success" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <span>🧭</span> 量化專家戰略導航 (Expert Strategic Guidance)
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MDTypography variant="button" fontWeight="bold" color="warning" display="block">🟡 XAUUSD (黃金)</MDTypography>
                  <MDTypography variant="caption" color="text">高頻 H1 + 低頻 D1 全風險檔位</MDTypography>
                  <MDTypography variant="caption" display="block" sx={{ fontStyle: "italic", opacity: 0.8 }}>高波動 · 高潛力報酬</MDTypography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MDTypography variant="button" fontWeight="bold" color="info" display="block">🔵 EURUSD / GBPUSD</MDTypography>
                  <MDTypography variant="caption" color="text">EURUSD 全頻率；GBPUSD 限 H1 + M5</MDTypography>
                  <MDTypography variant="caption" display="block" sx={{ fontStyle: "italic", opacity: 0.8 }}>穩定流動性 · MDD 控制嚴</MDTypography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MDTypography variant="button" fontWeight="bold" color="warning" display="block">🟡 USDJPY / USDCAD / USDCHF / AUDUSD</MDTypography>
                  <MDTypography variant="caption" color="text">高頻 H1 + 低頻 D1 全風險檔位</MDTypography>
                  <MDTypography variant="caption" display="block" sx={{ fontStyle: "italic", opacity: 0.8 }}>趨勢跟蹤 · 低 MDD 優先</MDTypography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MDTypography variant="button" fontWeight="bold" color="dark" display="block">💡 外部池核心原則</MDTypography>
                  <MDTypography variant="caption" color="text">用實際 MDD 定義低中高風險</MDTypography>
                  <MDTypography variant="caption" display="block" sx={{ fontWeight: "bold", color: "#2DD39B" }}>使用者選風險 = 看 MDD 區間！</MDTypography>
                </Grid>
              </Grid>
            </MDBox>
          </Grid>

          {/* ── 頂部：啟動控制台 ── */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={2.5} px={3}
                variant="gradient" bgColor="dark" borderRadius="lg" coloredShadow="dark"
              >
                <MDTypography variant="h5" color="white" fontWeight="bold">
                  🏭 Beast AI 策略銀行工廠
                </MDTypography>
                <MDTypography variant="caption" color="white" sx={{ opacity: 0.75 }}>
                  批量生產 36 類種子策略 · 9 宮格矩陣 · KPI 自動篩選
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                <Grid container spacing={3}>
                  {/* 貨幣對選擇 */}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>📊 貨幣對</MDTypography>
                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                      {SYMBOLS.map(s => {
                        // 「這個商品被整個停用」＝後端 SYMBOL_FREQUENCIES 給它空陣列。
                        //   工廠會跳過它、job 直接以 totalCells=0 結束 —— 但在這裡看不出來，
                        //   chip 長得跟能跑的一模一樣。2026-09-16 已停用 4 個（油/天然氣/英/德），
                        //   佔商品池 1/4，勾了只是空轉。
                        //   後端 /config 還沒回來時（map 是空的）不亂標，一律當成可用。
                        const off = Object.keys(symbolFreqs).length > 0 && (symbolFreqs[s] || []).length === 0;
                        return (
                          <Tooltip key={s}
                            title={off ? "目前停用：後端未開放任何頻率，勾了也不會產生任何 cell（原因見 factoryEngine.js 的 SYMBOL_FREQUENCIES 註解）" : ""}
                            placement="top">
                            <span>
                              <Chip label={off ? `${s}（停用）` : s} clickable={!off}
                                variant={customSym[s] && !off ? "filled" : "outlined"}
                                color={customSym[s] && !off ? "info" : "default"}
                                sx={off ? { opacity: 0.35, cursor: "not-allowed" } : {}}
                                onClick={() => { if (!off) setCustomSym(p => ({ ...p, [s]: !p[s] })); }}
                              />
                            </span>
                          </Tooltip>
                        );
                      })}
                    </MDBox>
                  </Grid>
                  {/* 風險等級：兩種模式都自動分類，無需手動選擇 */}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>🎯 風險等級</MDTypography>
                    <MDBox sx={{ p: 1.5, bgcolor: audience === "INTERNAL" ? "#e8eaf6" : "#e8f5e9", borderRadius: 2, border: `1px solid ${audience === "INTERNAL" ? "#9fa8da" : "#a5d6a7"}` }}>
                      <MDTypography variant="caption" fontWeight="bold" display="block" sx={{ color: audience === "INTERNAL" ? "#1a237e" : COLORS.safe }}>
                        ✅ 自動分類模式
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: COLORS.grayDark }}>
                        {audience === "INTERNAL"
                          ? "內部池依 KPI 目標 MDD 邊界自動分入低/中/高風險"
                          : "外部池依回測實際 MDD 自動分入低/中/高風險"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  {/* 頻率 */}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                      ⏱️ 頻率
                      <span style={{ fontSize: "11px", color: COLORS.grayMedian, fontWeight: 400, marginLeft: 6 }}>
                        (H1 + M15 + M5/M1 測試)
                      </span>
                    </MDTypography>
                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                      {FREQS.map(f => {
                        // INTERNAL 模式下禁用 M1/M5 測試頻率
                        const disabled = audience === "INTERNAL" && (f === "M1_FREQ" || f === "M5_FREQ");
                        // 計算哪些已選商品不支援此頻率
                        const selectedSyms = SYMBOLS.filter(s => customSym[s]);
                        const unsupportedSyms = selectedSyms.filter(s => {
                          // 後端還沒回來時不亂猜（給空陣列會誤標成全部不支援）→ 先當成都支援
                          const allowed = symbolFreqs[s] || (Object.keys(symbolFreqs).length ? [] : FREQS);
                          return !allowed.includes(f);
                        });
                        const hasUnsupported = unsupportedSyms.length > 0;
                        const tooltip = hasUnsupported
                          ? `${unsupportedSyms.join("、")} 不支援此頻率（後端會自動跳過）`
                          : "";
                        return (
                          <Tooltip key={f} title={tooltip} placement="top">
                            <span>
                              <Chip label={
                                <span>
                                  {FREQ_LABELS[f]}
                                  {hasUnsupported && (
                                    <span style={{ fontSize: "10px", marginLeft: 4, color: COLORS.warning }}>
                                      ⚠️{unsupportedSyms.join("/")}跳過
                                    </span>
                                  )}
                                </span>
                              } clickable={!disabled}
                                variant={customFreq[f] && !disabled ? "filled" : "outlined"}
                                color={customFreq[f] && !disabled ? "success" : "default"}
                                sx={disabled ? { opacity: 0.35, cursor: "not-allowed" } : {}}
                                onClick={() => { if (!disabled) setCustomFreq(p => ({ ...p, [f]: !p[f] })); }}
                              />
                            </span>
                          </Tooltip>
                        );
                      })}
                    </MDBox>
                    <MDTypography variant="caption" sx={{ color: COLORS.grayDark, mt: 0.5, display: "block" }}>
                      ⚠️ XAUUSD / XAGUSD / GBPUSD 僅支援 H1 + M5，M15 已停用<br/>
                      🧪 M5/M1 為模擬倉測試模式（訓練期短，快速驗證回測一致性）
                    </MDTypography>
                  </Grid>
                </Grid>

                {/* ── 受眾切換（INTERNAL / EXTERNAL）── */}
                <MDBox mt={2} p={2} sx={{ bgcolor: COLORS.grayLightest, borderRadius: 2, border: "1px solid #e0e0e0" }}>
                  <MDTypography variant="subtitle2" fontWeight="bold" mb={1.5}>🎯 策略池選擇</MDTypography>
                  <MDBox display="flex" gap={2}>
                    {[
                      { key: "EXTERNAL",   label: "🚀 外部客戶池", desc: "12個月訓練 + 60天OOS，傳統流程", color: COLORS.error },
                      { key: "INTERNAL",   label: "🏦 內部自營池", desc: "穩健低回撤、年化稍低",             color: "#1a237e" },
                      { key: "RPO_RECENT", label: "🔥 即時市場池", desc: "近期擬合 + 穩定性回顧（適合週更）", color: "#ff6f00" },
                    ].map(opt => (
                      <MDBox
                        key={opt.key}
                        onClick={() => {
                          setAudience(opt.key);
                          // INTERNAL 不跑 M1/M5；EXTERNAL 切換時保留 M5/M1 選擇
                          if (opt.key === "INTERNAL") {
                            setCustomFreq({ HIGH_FREQ: true, LOW_FREQ: true, M5_FREQ: false, M1_FREQ: false });
                          }
                        }}
                        sx={{
                          flex: 1, p: 1.5, borderRadius: 2, cursor: "pointer",
                          border: `2px solid ${audience === opt.key ? opt.color : "#ddd"}`,
                          bgcolor: audience === opt.key ? `${opt.color}11` : COLORS.white,
                          transition: "all 0.2s",
                        }}
                      >
                        <MDTypography variant="button" fontWeight="bold" display="block" sx={{ color: audience === opt.key ? opt.color : COLORS.grayDark }}>
                          {opt.label}
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: COLORS.grayDark }}>{opt.desc}</MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>

                  {/* 🔥 RPO 模式專屬：清空舊 elites 按鈕 */}
                  {audience === "RPO_RECENT" && (
                    <MDBox mt={1.5} display="flex" alignItems="center" gap={1}>
                      <MDTypography variant="caption" sx={{ color: "#ff6f00", flex: 1 }}>
                        💡 RPO 是週更模式。建議在重新訓練前封存舊 elites，避免新策略被歷史相似策略擋下。
                      </MDTypography>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ color: "#ff6f00", borderColor: "#ff6f00", fontSize: "11px", textTransform: "none", whiteSpace: "nowrap" }}
                        onClick={async () => {
                          const symList = SYMBOLS.filter(s => customSym[s]);
                          const freqList = FREQS.filter(f => customFreq[f]);
                          if (!symList.length || !freqList.length) {
                            alert("請先選擇貨幣對和頻率");
                            return;
                          }
                          if (!window.confirm(`確定要封存 RPO_RECENT 池子中以下策略的全部舊 elites？\n商品: ${symList.join(", ")}\n頻率: ${freqList.map(f => FREQ_LABELS[f]).join(", ")}\n\n（不會刪除，只是設為 ARCHIVED，未來可手動恢復）`)) return;
                          let total = 0;
                          for (const sym of symList) {
                            for (const freq of freqList) {
                              const r = await fetch("/api/admin/bank-factory/elites/archive-batch", {
                                method: "POST",
                                headers: headers(),
                                body: JSON.stringify({ audience: "RPO_RECENT", symbol: sym, frequency: freq }),
                              });
                              const d = await r.json();
                              total += d.archivedCount || 0;
                            }
                          }
                          alert(`✅ 已封存 ${total} 個 RPO 舊 elites`);
                        }}
                      >
                        🗑️ 封存舊 RPO Elites
                      </Button>
                    </MDBox>
                  )}
                </MDBox>

                {/* KPI 達標標準展示 */}
                <MDBox mt={2} p={2} sx={{ bgcolor: COLORS.grayLightest, borderRadius: 2 }}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="caption" fontWeight="bold" display="block">
                      📋 KPI 考核標準 ({audience === "INTERNAL" ? "🏦 內部自營" : audience === "RPO_RECENT" ? "🔥 即時市場(RPO)" : "🚀 外部客戶"})
                    </MDTypography>
                    {(() => {
                      const selectedSyms = Object.keys(customSym).filter(s => customSym[s]);
                      const activeSym = selectedSyms.length === 1 ? selectedSyms[0] : "DEFAULT";
                      const isCustom = activeSym !== "DEFAULT";
                      return <Chip
                        label={isCustom ? `✨ 當前：${activeSym} 專屬標準` : "🌐 適用：通用標準"}
                        size="small"
                        color={isCustom ? "warning" : "secondary"}
                        sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }}
                      />;
                    })()}
                  </MDBox>
                  <MDBox display="grid" sx={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                    {RISKS.map(r => (
                      <MDBox key={r} sx={{ p: 1.5, bgcolor: COLORS.white, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: `1px solid ${RISK_COLORS[r]}22` }}>
                        <MDTypography variant="button" fontWeight="bold" display="block" mb={1} sx={{ color: RISK_COLORS[r], borderBottom: `2px solid ${RISK_COLORS[r]}22`, pb: 0.5 }}>
                          {RISK_LABELS[r]}
                        </MDTypography>
                        {FREQS.map(f => {
                          const selectedSyms = Object.keys(customSym).filter(s => customSym[s]);
                          const activeSym = selectedSyms.length === 1 ? selectedSyms[0] : "DEFAULT";
                          const activeConfig = kpiConfigs[audience] || symbolsKpiConfig;
                          const config = activeConfig?.[activeSym] || activeConfig?.["DEFAULT"];
                          const cell = config?.[r]?.[f];
                          const isOverridden = activeSym !== "DEFAULT" && activeConfig?.[activeSym]?.[r]?.[f];

                          const renderMetric = (label, key, unit) => {
                            const rule = cell?.[key];
                            if (!rule) return "—";
                            return (
                              <span style={{ color: isOverridden ? "#fb8c00" : "inherit", fontWeight: isOverridden ? "bold" : "inherit" }}>
                                {label} {rule.type === 'gt' ? '>' : '<'}{rule.value}{unit}
                              </span>
                            );
                          };

                          return (
                            <MDBox key={f} mb={1.5}>
                              <MDTypography variant="caption" fontWeight="bold" display="block" sx={{ fontSize: "0.95rem", color: COLORS.black, mb: 0.2 }}>
                                ● {FREQ_LABELS[f].split(" ")[1]}
                              </MDTypography>
                              <MDBox sx={{ pl: 1.5 }}>
                                <MDTypography variant="caption" sx={{ fontSize: "0.9rem", color: COLORS.black, lineHeight: 1.5, fontWeight: 500 }}>
                                  {renderMetric("回報", "returnRatePct", "%")} | {renderMetric("MDD", "maxDrawdownPct", "%")} | {renderMetric("PF", "profitFactor", "")} {cell?.calmarRatio ? `| ${renderMetric("Calmar", "calmarRatio", "")}` : ""}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          );
                        })}
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>

                <MDBox mt={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <Button
                    variant="contained"
                    disabled={launching}
                    onClick={handleLaunch}
                    sx={{
                      bgcolor: "#1a237e", color: COLORS.white, px: 4, py: 1.2, fontWeight: 700,
                      "&:hover": { bgcolor: "#283593" },
                      "&:disabled": { bgcolor: COLORS.grayDark }
                    }}
                  >
                    {launching
                      ? <><CircularProgress size={16} sx={{ mr: 1, color: COLORS.white }} /> 啟動中...</>
                      : (burnHours > 0 ? `🔥 連續燒 ${burnHours} 小時` : "🚀 啟動工廠")}
                  </Button>

                  {/* 🔥 連續燒：一輪跑完自動接下一輪，直到時數到（實測工廠 ~US$3/小時） */}
                  <MDBox display="flex" alignItems="center" gap={1}
                    sx={{ border: "1px solid #ffcc80", bgcolor: "#fff8e1", borderRadius: 1.5, px: 1.5, py: 0.75 }}>
                    <Tooltip title="設定後：跑完全部商品若還沒到時間，會自動再開下一輪繼續跑，直到時數用完。要提早停就按當前任務的「取消」。實測約 US$3/小時。">
                      <MDTypography variant="button" fontWeight="bold" sx={{ color: "#e65100", cursor: "help" }}>
                        🔥 連續燒
                      </MDTypography>
                    </Tooltip>
                    <Select
                      size="small"
                      value={burnHours}
                      onChange={(e) => setBurnHours(Number(e.target.value))}
                      sx={{ minWidth: 110, bgcolor: COLORS.white, fontSize: 13 }}
                    >
                      <MenuItem value={0}>關閉（跑一輪）</MenuItem>
                      <MenuItem value={1}>1 小時</MenuItem>
                      <MenuItem value={2}>2 小時</MenuItem>
                      <MenuItem value={3}>3 小時</MenuItem>
                      <MenuItem value={6}>6 小時</MenuItem>
                      <MenuItem value={12}>12 小時</MenuItem>
                    </Select>
                    {burnHours > 0 && (
                      <MDTypography variant="caption" sx={{ color: "#8d6e63" }}>
                        ≈ US${(burnHours * 3).toFixed(0)}
                      </MDTypography>
                    )}
                  </MDBox>
                  <Button variant="outlined" onClick={fetchJobs} sx={{ fontWeight: 600, color: COLORS.black, borderColor: '#ddd' }}>
                    🔄 重新整理
                  </Button>
                  <Tooltip title="重新用最新 KPI 標準分類所有歷史候選策略，符合條件者自動晉升精英庫">
                    <Button
                      variant="outlined"
                      disabled={reclassifying}
                      onClick={handleReclassify}
                      sx={{ fontWeight: 600, color: '#7b1fa2', borderColor: '#ce93d8', "&:hover": { bgcolor: "#f3e5f5" } }}
                    >
                      {reclassifying ? <><CircularProgress size={14} sx={{ mr: 1, color: "#7b1fa2" }} /> 重新分類中...</> : "🔄 重新分類候選庫"}
                    </Button>
                  </Tooltip>
                  {/* 🚀 LIVE 模擬倉狀態 + 📦 EXE 打包都已移到 Beast 菁英策略庫頁面
                       架構：工廠生產（KPI 矩陣 + 重新分類）/ 菁英庫管理（LIVE + EXE + 活躍狀態）*/}
                </MDBox>

                {/* ── reclassify 進度顯示 ── */}
                {reclassifyStatus && reclassifyStatus.status !== "IDLE" && (
                  <MDBox mt={1.5} p={1.5} sx={{
                    bgcolor: reclassifyStatus.status === "DONE" ? "#e8f5e9"
                      : (reclassifyStatus.status === "FAILED" || reclassifyStatus.status === "TIMEOUT") ? "#ffebee"
                      : "#f3e5f5",
                    borderRadius: 2,
                    border: `1px solid ${reclassifyStatus.status === "DONE" ? "#a5d6a7"
                      : (reclassifyStatus.status === "FAILED" || reclassifyStatus.status === "TIMEOUT") ? "#ef9a9a"
                      : "#ce93d8"}`
                  }}>
                    <MDTypography variant="caption" fontWeight="bold" display="block" sx={{
                      color: reclassifyStatus.status === "DONE" ? COLORS.safe
                        : (reclassifyStatus.status === "FAILED" || reclassifyStatus.status === "TIMEOUT") ? COLORS.error
                        : "#6a1b9a"
                    }}>
                      {reclassifyStatus.status === "QUEUED"  && "⏳ 排隊中，等待 Worker 處理..."}
                      {reclassifyStatus.status === "RUNNING" && "🔄 正在重新分類候選策略庫..."}
                      {reclassifyStatus.status === "DONE"    && "✅ 重新分類完成！"}
                      {reclassifyStatus.status === "FAILED"  && `❌ 失敗：${reclassifyStatus.error}`}
                      {reclassifyStatus.status === "TIMEOUT" && `⏰ 逾時：${reclassifyStatus.error}`}
                    </MDTypography>
                    {reclassifyStatus.status === "DONE" && reclassifyStatus.result && (
                      <MDBox display="flex" gap={2} mt={0.5} flexWrap="wrap">
                        {[
                          { label: "總候選", value: reclassifyStatus.result.total },
                          { label: "已檢查", value: reclassifyStatus.result.checked },
                          { label: "訓練達標", value: reclassifyStatus.result.requalified },
                          { label: "晉升精英", value: reclassifyStatus.result.promoted, color: COLORS.safe },
                          { label: "跳過", value: reclassifyStatus.result.skipped },
                        ].map(s => (
                          <MDBox key={s.label} sx={{ textAlign: "center", minWidth: 60 }}>
                            <MDTypography variant="h6" fontWeight="bold" sx={{ color: s.color || COLORS.grayDark }}>{s.value}</MDTypography>
                            <MDTypography variant="caption" sx={{ color: COLORS.grayDark }}>{s.label}</MDTypography>
                          </MDBox>
                        ))}
                      </MDBox>
                    )}
                    {reclassifyStatus.status === "RUNNING" && (
                      <LinearProgress sx={{ mt: 1, borderRadius: 2, bgcolor: "#e1bee7" }} color="secondary" />
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* ── Job 列表 ── */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="bold" mb={2}>📋 任務記錄</MDTypography>
                {loading && <CircularProgress size={20} />}
                {jobs.length === 0 && !loading && (
                  <MDTypography variant="body2" sx={{ color: COLORS.grayMedian }}>尚無任務記錄，請啟動工廠</MDTypography>
                )}
                {jobs.map(job => {
                  const pct = job.totalCells > 0 ? Math.round((job.completedCells / job.totalCells) * 100) : 0;
                  const isActive = activeJob === job.id;
                  return (
                    <MDBox key={job.id} mb={1.5}
                      onClick={() => handleViewJob(job.id)}
                      sx={{
                        p: 1.5, borderRadius: 2, cursor: "pointer",
                        border: `2px solid ${isActive ? "#1a237e" : COLORS.grayLighter}`,
                        bgcolor: isActive ? "#e8eaf6" : COLORS.grayLightest,
                        transition: "all 0.2s",
                        "&:hover": { borderColor: "#3949ab", bgcolor: "#f3f4f8" },
                      }}
                    >
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDBox display="flex" gap={0.5} alignItems="center">
                          {statusChip(job.status)}
                          <Chip
                            label={job.audience === "INTERNAL" ? "🏦 內部" : "🚀 外部"}
                            size="small"
                            sx={{
                              bgcolor: job.audience === "INTERNAL" ? "#1a237e" : COLORS.error,
                              color: COLORS.white, fontWeight: 700, fontSize: "0.65rem", height: 20
                            }}
                          />
                          {/* 🔥 連續燒任務：顯示剩餘時間（截止時間編碼在 triggeredBy） */}
                          {(() => {
                            const m = /\|burn:(\d+)/.exec(job.triggeredBy || "");
                            if (!m) return null;
                            const leftMin = Math.round((Number(m[1]) - Date.now()) / 60000);
                            return (
                              <Chip
                                label={leftMin > 0 ? `🔥 燒 剩${leftMin}分` : "🔥 燒 已結束"}
                                size="small"
                                sx={{
                                  bgcolor: leftMin > 0 ? "#e65100" : COLORS.grayDark,
                                  color: COLORS.white, fontWeight: 700, fontSize: "0.65rem", height: 20
                                }}
                              />
                            );
                          })()}
                        </MDBox>
                        <MDTypography variant="caption" sx={{ color: COLORS.grayMedian }}>{fmtDate(job.createdAt)}</MDTypography>
                      </MDBox>
                      <MDTypography variant="caption" display="block" mt={0.5} sx={{ color: COLORS.black, fontWeight: 500, fontSize: "0.9rem", wordBreak: "break-all" }}>
                        {job.id.slice(0, 18)}...
                      </MDTypography>
                      {job.status === "RUNNING" && (
                        <MDBox mt={0.5}>
                          <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
                          <MDTypography variant="caption" sx={{ color: "#1a237e", fontSize: "0.85rem", fontWeight: "bold" }}>
                            {job.completedCells}/{job.totalCells} 格 · {pct}%
                          </MDTypography>
                          {job.currentCell && (
                            <MDTypography variant="caption" display="block" sx={{ color: COLORS.black, fontSize: "0.8rem", fontWeight: 500 }}>
                              最新動態：{job.currentCell} (第 {job.currentCellIter} 次)
                            </MDTypography>
                          )}
                        </MDBox>
                      )}
                      {job.status === "DONE" && (
                        <MDTypography variant="caption" sx={{ color: COLORS.safe, fontWeight: 700 }}>
                          ✅ 達標：{job.qualifiedCount}/{job.totalCells} 格
                        </MDTypography>
                      )}
                      {job.status === "CANCELLED" && (
                        <MDTypography variant="caption" sx={{ color: COLORS.warning, fontWeight: 700 }}>
                          🛑 取消時進度：{job.completedCells}/{job.totalCells} 格 (達標：{job.qualifiedCount})
                        </MDTypography>
                      )}
                    </MDBox>
                  );
                })}
              </MDBox>
            </Card>
          </Grid>

          {/* ── Job 詳情 + 9宮格 ── */}
          <Grid item xs={12} md={8}>
            <Card>
              <MDBox p={2}>
                {!jobDetail ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                    <MDTypography variant="h6" sx={{ color: "#bbb" }}>👈 點選左側任務查看詳情</MDTypography>
                  </MDBox>
                ) : (
                  <>
                    <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">任務詳情</MDTypography>
                        <MDTypography variant="caption" sx={{ color: COLORS.grayMedian }}>{jobDetail.id}</MDTypography>
                      </MDBox>
                      <MDBox display="flex" gap={1}>
                        {statusChip(jobDetail.status)}
                        {(jobDetail.status === "QUEUED" || jobDetail.status === "RUNNING") && (
                          <Chip label={cancelling ? "⏳ 取消中..." : "🛑 取消"} size="small" clickable={!cancelling}
                            onClick={() => !cancelling && handleCancel(jobDetail.id)}
                            sx={{ bgcolor: COLORS.lighter, color: "#e65100", fontWeight: 600 }}
                          />
                        )}
                        <Chip label="🗑️ 刪除" size="small" clickable
                          onClick={() => handleDelete(jobDetail.id)}
                          sx={{ bgcolor: "#ffebee", color: COLORS.error, fontWeight: 600 }}
                        />
                      </MDBox>
                    </MDBox>

                    {/* 進度條 */}
                    {jobDetail.status === "RUNNING" && (
                      <MDBox mb={2}>
                        <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                          <MDTypography variant="caption" fontWeight="bold">
                            生產進度：{jobDetail.completedCells}/{jobDetail.totalCells}
                          </MDTypography>
                          <MDTypography variant="caption">
                            {Math.round((jobDetail.completedCells / jobDetail.totalCells) * 100)}%
                          </MDTypography>
                        </MDBox>
                        <LinearProgress
                          variant="determinate"
                          value={Math.round((jobDetail.completedCells / jobDetail.totalCells) * 100)}
                          sx={{ height: 10, borderRadius: 5 }}
                        />
                        {jobDetail.currentCell && (
                          <MDBox mt={0.5} p={1} sx={{ bgcolor: "#e3f2fd", borderRadius: 1 }}>
                            <MDTypography variant="caption" sx={{ color: "#1565c0" }}>
                              🔄 當前格子：<strong>{jobDetail.currentCell}</strong> · 第 {jobDetail.currentCellIter} 次迭代
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    )}

                    {/* 統計數字 */}
                    <MDBox display="flex" gap={2} mb={2} flexWrap="wrap">
                      {[
                        { label: "總格子數", value: jobDetail.totalCells, color: "#1a237e" },
                        { label: "已完成", value: jobDetail.completedCells, color: "#2196f3" },
                        { label: "達標策略", value: jobDetail.qualifiedCount, color: COLORS.safe },
                        { label: "候選總數", value: jobDetail.totalCandidates || 0, color: COLORS.warning },
                      ].map(s => (
                        <MDBox key={s.label} sx={{ p: 1.5, bgcolor: COLORS.grayLightest, borderRadius: 2, textAlign: "center", minWidth: 80 }}>
                          <MDTypography variant="h5" fontWeight="bold" sx={{ color: s.color }}>{s.value}</MDTypography>
                          <MDTypography variant="caption" sx={{ color: COLORS.grayDark }}>{s.label}</MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>

                    {/* 9 宮格矩陣 */}
                    <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                      📊 9 宮格策略矩陣（點擊格子查看代碼）
                    </MDTypography>
                    <Matrix9 candidates={jobDetail.candidates || []} jobSymbols={jobDetail.symbols || []} jobAudience={jobDetail.audience || "EXTERNAL"} />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ── 代碼查看 Dialog ── */}
      <Dialog open={codeDialog.open} onClose={() => setCodeDialog({ open: false, data: null, fetching: false })}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: "#1a237e", color: COLORS.white, fontWeight: 700 }}>
          {codeDialog.data
            ? `📄 ${codeDialog.data.symbol} · ${RISK_LABELS[codeDialog.data.riskLevel]} · ${FREQ_LABELS[codeDialog.data.frequency]}`
            : "📄 策略代碼"}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {codeDialog.fetching && (
            <MDBox display="flex" justifyContent="center" py={4}><CircularProgress /></MDBox>
          )}
          {!codeDialog.fetching && codeDialog.data && (
            <>
              {/* 🎯 KPI 三欄對照表：規則門檻 vs 訓練期 vs 期末考 */}
              <MDBox p={2} sx={{ bgcolor: COLORS.grayLightest, borderBottom: "1px solid #eee" }}>
                <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
                  <MDTypography variant="button" fontWeight="bold" sx={{ color: "#1a237e" }}>
                    📊 KPI 完整對照
                  </MDTypography>
                  <Chip
                    label={codeDialog.data.isQualified ? "✅ 已晉升精英" : "⚠️ 候選未晉升"}
                    size="small"
                    sx={{
                      bgcolor: codeDialog.data.isQualified ? "#e8f5e9" : COLORS.lighter,
                      color: codeDialog.data.isQualified ? COLORS.safe : "#e65100", fontWeight: 700
                    }}
                  />
                  {codeDialog.data.isPotential && (
                    <Chip
                      label="🌱 潛力好苗子（孵化池）"
                      size="small"
                      sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 700 }}
                    />
                  )}
                </MDBox>

                {/* 三欄對照表：規則 | 訓練期實際 | 期末考實際 */}
                {(() => {
                  // 對每個 metric 渲染一個 row
                  const metrics = [
                    { label: "📈 Return", key: "returnRatePct",  unit: "%", op: "gt", decimals: 1 },
                    { label: "📉 MDD",    key: "maxDrawdownPct", unit: "%", op: "lt", decimals: 1 },
                    { label: "💪 PF",     key: "profitFactor",   unit: "",  op: "gt", decimals: 2 },
                    { label: "⚖️ Calmar", key: "calmarRatio",    unit: "",  op: "gt", decimals: 2 },
                    { label: "🎯 RRR",    key: "riskRewardRatio",unit: "",  op: "gt", decimals: 2 },
                    { label: "🛡️ Sortino",key: "sortinoRatio",   unit: "",  op: "gt", decimals: 2 },
                    { label: "🎲 勝率",   key: "winRatePct",     unit: "%", op: "info", decimals: 1 },
                    { label: "🔢 交易數", key: "trades",         unit: "",  op: "info", decimals: 0 },
                  ];
                  const trainKpi = codeDialog.data.kpi || {};
                  const oosKpi   = codeDialog.data.oosKpi || null;
                  const targets  = codeDialog.data.kpiTargets || null;
                  const riskTier = codeDialog.data.riskLevel || "LOW";
                  const symbol   = codeDialog.data.symbol || "DEFAULT";
                  const frequency = codeDialog.data.frequency || "LOW_FREQ";

                  // 🎯 方案 B+：每幣別獨立的 OOS 期末考門檻（後端回傳）
                  const oosBackend = codeDialog.data.oosTargets || { returnMin: 0, pfMin: 1.0 };
                  const oosTargets = {
                    returnRatePct:  { enabled: true,  type: "gt", value: oosBackend.returnMin },
                    profitFactor:   { enabled: true,  type: "gt", value: oosBackend.pfMin },
                    maxDrawdownPct: targets?.maxDrawdownPct,  // OOS MDD 沿用訓練期上限
                  };

                  // 🧮 OOS 期間天數（用於年化換算）
                  const oosDaysMap = { HIGH_FREQ: 45, LOW_FREQ: 60, M5_FREQ: 14, M1_FREQ: 7 };
                  const oosDays = oosDaysMap[frequency] || 60;
                  // 年化 CAGR 公式：((1 + r/100)^(365/天) - 1) × 100
                  const annualize = (ret, days) => {
                    if (ret == null || days <= 0) return null;
                    return ((Math.pow(1 + ret / 100, 365 / days) - 1) * 100);
                  };

                  const fmt = (v, dec) => (v == null ? "—" : Number(v).toFixed(dec));
                  const passCheck = (val, targetCfg) => {
                    if (!targetCfg?.enabled || val == null) return null;
                    if (targetCfg.type === "gt") return val >= targetCfg.value;
                    if (targetCfg.type === "lt") return val <= targetCfg.value;
                    return null;
                  };
                  const cellSx = (pass) => ({
                    py: 0.5, px: 1, borderRadius: 1,
                    bgcolor: pass === true ? "#e8f5e9" : pass === false ? "#ffebee" : "transparent",
                    color: pass === true ? COLORS.safe : pass === false ? COLORS.error : "inherit",
                    fontWeight: pass != null ? "bold" : "normal",
                    fontSize: "0.85rem", minWidth: 70, textAlign: "center"
                  });

                  return (
                    <MDBox sx={{ border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
                      {/* 表頭 */}
                      <MDBox display="grid" sx={{
                        gridTemplateColumns: "100px 1fr 1fr 1fr",
                        bgcolor: "#1a237e", fontWeight: "bold", fontSize: "0.8rem"
                      }}>
                        <MDBox sx={{ p: 1, color: "#fff !important" }}>
                          <span style={{ color: COLORS.white }}>指標</span>
                        </MDBox>
                        <MDBox sx={{ p: 1, textAlign: "center" }}>
                          <span style={{ color: COLORS.white }}>🎯 訓練期門檻</span>
                        </MDBox>
                        <MDBox sx={{ p: 1, textAlign: "center" }}>
                          <span style={{ color: COLORS.white }}>📖 訓練期實際</span>
                        </MDBox>
                        <MDBox sx={{ p: 1, textAlign: "center", display: "flex", flexDirection: "column" }}>
                          <span style={{ color: COLORS.white }}>🎓 期末考實際 ({oosDays}天)</span>
                          <span style={{ fontSize: "0.65rem", fontWeight: "normal", color: COLORS.white, opacity: 0.85 }}>
                            ({symbol} {riskTier}: ret≥{oosBackend.returnMin}%, PF≥{oosBackend.pfMin})
                          </span>
                        </MDBox>
                      </MDBox>
                      {/* 表身 */}
                      {metrics.map(({ label, key, unit, op, decimals }) => {
                        const targetCfg    = targets?.[key];            // 訓練期門檻
                        const oosTargetCfg = oosTargets?.[key];          // OOS 期末考門檻（方案 B 階梯式，僅限 Return/PF/MDD）
                        const trainVal = trainKpi[key];
                        const oosVal   = oosKpi?.[key];
                        const trainPass = passCheck(trainVal, targetCfg);
                        const oosPass   = passCheck(oosVal, oosTargetCfg || targetCfg);  // OOS 優先用 OOS 專屬門檻，其餘指標沿用訓練門檻
                        const ruleText = targetCfg?.enabled
                          ? `${targetCfg.type === "gt" ? ">" : "<"} ${targetCfg.value}${unit}`
                          : (op === "info" ? "(資訊)" : "—");
                        const oosRuleText = oosTargetCfg?.enabled
                          ? `（OOS: ${oosTargetCfg.type === "gt" ? ">" : "<"} ${oosTargetCfg.value}${unit}）`
                          : "";
                        return (
                          <MDBox key={key} display="grid" sx={{
                            gridTemplateColumns: "100px 1fr 1fr 1fr",
                            borderTop: "1px solid #eee",
                            "&:nth-of-type(even)": { bgcolor: COLORS.grayLightest }
                          }}>
                            <MDBox sx={{ p: 1, fontWeight: "bold", fontSize: "0.85rem" }}>{label}</MDBox>
                            <MDBox sx={{ p: 1, textAlign: "center", fontFamily: "monospace", fontSize: "0.85rem", color: COLORS.grayDark }}>
                              {ruleText}
                            </MDBox>
                            <MDBox sx={{ p: 0.5, display: "flex", justifyContent: "center" }}>
                              <MDBox sx={cellSx(trainPass)}>
                                {fmt(trainVal, decimals)}{unit}
                              </MDBox>
                            </MDBox>
                            <MDBox sx={{ p: 0.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
                              <MDBox sx={cellSx(oosPass)}>
                                {oosVal == null ? "—" : `${fmt(oosVal, decimals)}${unit}`}
                              </MDBox>
                              {/* Return 欄位額外顯示年化（CAGR） */}
                              {key === "returnRatePct" && oosVal != null && (
                                <span style={{ fontSize: "0.65rem", color: "#1976d2", fontFamily: "monospace" }}>
                                  年化 ≈ {annualize(oosVal, oosDays).toFixed(1)}%
                                </span>
                              )}
                            </MDBox>
                          </MDBox>
                        );
                      })}
                    </MDBox>
                  );
                })()}

                {/* 說明 */}
                <MDTypography variant="caption" sx={{ color: COLORS.grayDark, mt: 1, display: "block" }}>
                  💡 綠色=通過該指標、紅色=不通過、灰色=資訊欄不參與判定。
                  {!codeDialog.data.oosKpi && !codeDialog.data.sourceLabel && " 期末考欄位為「—」表示此策略未通過訓練期，沒進行 OOS。"}
                  {!codeDialog.data.oosKpi && codeDialog.data.sourceLabel === "incubator" && " 此策略在孵化池但 OOS 資料遺失（舊版資料）。"}
                  {codeDialog.data.sourceLabel === "elite" && " 📊 資料來源：精英庫 fullKpi"}
                  {codeDialog.data.sourceLabel === "incubator" && codeDialog.data.oosKpi && " 📊 資料來源：孵化池 failOosKpi（此策略 OOS 落榜但訓練優秀）"}
                </MDTypography>

                {/* 🧠 新增：OOS 樣本外嚴選報告 */}
                {codeDialog.data.metadata?.oos_kpi && (
                  <MDBox mt={2} p={1.5} sx={{ bgcolor: COLORS.white, borderRadius: 2, border: "1px dashed #1a237e" }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#1a237e" }}>
                        🎓 OOS 隱藏期末考報告 ({codeDialog.data.metadata.exam_range})
                      </MDTypography>
                      <Chip
                        icon={<Icon>stars</Icon>}
                        label={`穩定度: ${codeDialog.data.metadata.stability_score}%`}
                        size="small"
                        sx={{
                          bgcolor: codeDialog.data.metadata.stability_score >= 80 ? "#fff8e1" : "#e8f5e9",
                          color: codeDialog.data.metadata.stability_score >= 80 ? "#ffa000" : COLORS.safe,
                          fontWeight: "bold", border: "1px solid"
                        }}
                      />
                    </MDBox>
                    <MDBox display="flex" gap={3}>
                      <MDBox>
                        <MDTypography variant="caption" display="block" sx={{ color: COLORS.grayDark }}>近期獲利</MDTypography>
                        <MDTypography variant="button" fontWeight="bold" sx={{ color: codeDialog.data.metadata.oos_kpi.returnRatePct > 0 ? COLORS.safe : COLORS.error }}>
                          {codeDialog.data.metadata.oos_kpi.returnRatePct}%
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="caption" display="block" sx={{ color: COLORS.grayDark }}>近期回撤</MDTypography>
                        <MDTypography variant="button" fontWeight="bold">{codeDialog.data.metadata.oos_kpi.maxDrawdownPct}%</MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="caption" display="block" sx={{ color: COLORS.grayDark }}>近期 PF</MDTypography>
                        <MDTypography variant="button" fontWeight="bold">{codeDialog.data.metadata.oos_kpi.profitFactor}</MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="caption" display="block" sx={{ color: COLORS.grayDark }}>測試狀態</MDTypography>
                        <MDTypography variant="button" fontWeight="bold" color="success">✅ 通過嚴選</MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                )}
              </MDBox>
              {/* 代碼區 */}
              <MDBox
                component="pre"
                sx={{
                  m: 0, p: 2.5, bgcolor: "#1e1e2e", color: "#cdd6f4",
                  fontFamily: "monospace", fontSize: "0.75rem", overflow: "auto", maxHeight: 480,
                  whiteSpace: "pre-wrap", wordBreak: "break-all"
                }}
              >
                {codeDialog.data.code}
              </MDBox>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <MDButton onClick={() => setCodeDialog({ open: false, data: null, fetching: false })} color="secondary">
            關閉
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => {
              navigator.clipboard.writeText(codeDialog.data.code);
            }}
          >
            複製代碼
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            disabled={!codeDialog.data?.code}
            onClick={handleOpenLiveConvert}
          >
            🚀 轉 LIVE 模擬倉版本
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── 🚀 LIVE 轉換 Dialog ── */}
      <Dialog
        open={liveDialog.open}
        onClose={() => setLiveDialog(s => ({ ...s, open: false }))}
        maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: COLORS.safe, color: COLORS.white, fontWeight: 700 }}>
          🚀 轉換為 LIVE / 模擬倉版本
          {liveDialog.source && (
            <span style={{ fontSize: "0.85rem", fontWeight: 400, marginLeft: 12, opacity: 0.85 }}>
              {liveDialog.source.symbol} · {liveDialog.source.frequency}
            </span>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {/* 表單 */}
          <MDBox display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
            <MDBox>
              <MDTypography variant="caption" fontWeight="bold">下單手數 (volume)</MDTypography>
              <input
                type="number" step="0.01" min="0.01"
                value={liveDialog.form.volume}
                onChange={e => setLiveDialog(s => ({ ...s, form: { ...s.form, volume: e.target.value } }))}
                style={{ display: "block", width: 120, padding: "6px 10px", border: "1px solid #ccc", borderRadius: 6 }}
              />
            </MDBox>
            <MDBox>
              <MDTypography variant="caption" fontWeight="bold">起始本金 (capital)</MDTypography>
              <input
                type="number" min="100"
                value={liveDialog.form.capital}
                onChange={e => setLiveDialog(s => ({ ...s, form: { ...s.form, capital: e.target.value } }))}
                style={{ display: "block", width: 140, padding: "6px 10px", border: "1px solid #ccc", borderRadius: 6 }}
              />
            </MDBox>
            <MDBox flex={1} minWidth={260}>
              <MDTypography variant="caption" fontWeight="bold">輸出檔名（存到 server/paper_trade_live/）</MDTypography>
              <input
                type="text"
                value={liveDialog.form.filename}
                onChange={e => setLiveDialog(s => ({ ...s, form: { ...s.form, filename: e.target.value } }))}
                style={{ display: "block", width: "100%", padding: "6px 10px", border: "1px solid #ccc", borderRadius: 6 }}
              />
            </MDBox>
            <MDButton
              variant="gradient" color="success"
              disabled={liveDialog.converting}
              onClick={handleRunConvert}
              sx={{ alignSelf: "flex-end" }}
            >
              {liveDialog.converting ? "轉換中..." : "🔄 執行轉換"}
            </MDButton>
          </MDBox>

          {/* Patch 報表 */}
          {liveDialog.result?.report && (
            <MDBox mb={2} p={1.5} sx={{ bgcolor: COLORS.grayLightest, borderRadius: 1, border: "1px solid #e0e0e0" }}>
              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#1a237e", mb: 1, display: "block" }}>
                📋 Patch Report
              </MDTypography>
              <MDBox display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                {liveDialog.result.report.patches.map((p, i) => (
                  <Chip
                    key={i}
                    size="small"
                    label={`${p.applied ? "✅" : p.error ? "❌" : "⚠️"} ${p.name}`}
                    sx={{
                      bgcolor: p.applied ? "#e8f5e9" : p.error ? "#ffebee" : COLORS.lighter,
                      color: p.applied ? COLORS.safe : p.error ? COLORS.error : "#e65100",
                      fontWeight: 600
                    }}
                  />
                ))}
              </MDBox>
              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#1a237e", mb: 1, display: "block" }}>
                🔍 send_order 分類（共 {liveDialog.result.report.sendOrderSites.length} 處）
              </MDTypography>
              <MDBox sx={{ fontFamily: "monospace", fontSize: "0.78rem", lineHeight: 1.6 }}>
                {liveDialog.result.report.sendOrderSites
                  .slice()
                  .sort((a, b) => a.line - b.line)
                  .map((s, i) => (
                    <div key={i}>
                      {s.classification === "OPEN" ? "📈" : s.classification === "CLOSE" ? "📉" : s.classification === "SKIP" ? "⏭️" : "❓"}
                      &nbsp;L{s.line}&nbsp;&nbsp;
                      <strong>{s.classification}</strong>
                      &nbsp;&nbsp;<span style={{ color: COLORS.grayMedian }}>[{s.rule || "—"}]</span>
                    </div>
                  ))}
              </MDBox>
              {liveDialog.result.report.unclassified.length > 0 && (
                <MDBox mt={1.5} p={1} sx={{ bgcolor: "#ffebee", borderRadius: 1 }}>
                  <MDTypography variant="caption" sx={{ color: COLORS.error, fontWeight: 700 }}>
                    ⚠️ {liveDialog.result.report.unclassified.length} 處無法自動分類，需人工處理：
                  </MDTypography>
                  {liveDialog.result.report.unclassified.map((u, i) => (
                    <div key={i} style={{ fontFamily: "monospace", fontSize: "0.75rem", color: COLORS.error }}>
                      L{u.line}: {u.raw}
                    </div>
                  ))}
                </MDBox>
              )}
            </MDBox>
          )}

          {/* 左右對照：原始 vs LIVE */}
          {liveDialog.result?.liveCode && (
            <MDBox display="grid" sx={{ gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <MDBox>
                <MDTypography variant="caption" fontWeight="bold" sx={{ color: COLORS.grayDark }}>
                  📜 原始 backtest 代碼
                </MDTypography>
                <MDBox
                  component="pre"
                  sx={{
                    m: 0, p: 1.5, bgcolor: "#1e1e2e", color: "#cdd6f4",
                    fontFamily: "monospace", fontSize: "0.7rem", overflow: "auto",
                    maxHeight: 420, whiteSpace: "pre-wrap", wordBreak: "break-all",
                    borderRadius: 1
                  }}
                >
                  {liveDialog.source.code}
                </MDBox>
              </MDBox>
              <MDBox>
                <MDTypography variant="caption" fontWeight="bold" sx={{ color: COLORS.safe }}>
                  🚀 轉換後 LIVE 代碼
                </MDTypography>
                <MDBox
                  component="pre"
                  sx={{
                    m: 0, p: 1.5, bgcolor: "#0d3b1e", color: "#d4f5dc",
                    fontFamily: "monospace", fontSize: "0.7rem", overflow: "auto",
                    maxHeight: 420, whiteSpace: "pre-wrap", wordBreak: "break-all",
                    borderRadius: 1
                  }}
                >
                  {liveDialog.result.liveCode}
                </MDBox>
              </MDBox>
            </MDBox>
          )}

          {/* 儲存訊息 */}
          {liveDialog.saveMsg && (
            <MDBox mt={2} p={1.5} sx={{
              bgcolor: liveDialog.saveMsg.startsWith("✅") ? "#e8f5e9" : "#ffebee",
              borderRadius: 1, fontFamily: "monospace", fontSize: "0.8rem"
            }}>
              {liveDialog.saveMsg}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <MDButton onClick={() => setLiveDialog(s => ({ ...s, open: false }))} color="secondary">
            關閉
          </MDButton>
          <MDButton
            variant="outlined" color="info"
            disabled={!liveDialog.result?.liveCode}
            onClick={() => navigator.clipboard.writeText(liveDialog.result.liveCode)}
          >
            複製 LIVE 代碼
          </MDButton>
          <MDButton
            variant="gradient" color="success"
            disabled={!liveDialog.result?.liveCode || liveDialog.saving || !liveDialog.form.filename}
            onClick={handleSaveLive}
          >
            {liveDialog.saving ? "儲存中..." : "💾 存到 paper_trade_live/"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default BankFactory;
